<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div v-if="editedUser">
      <div class="row">
        <div class="col-md-12">
          <h3>{{ editedUser.name }}</h3>
          <p class="text-muted">{{ editedUser.email }}</p>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h4>{{ _('User group membership') }}</h4>
          <hr />
          <div class="form-group" v-if="allGroups.length > 8">
            <input class="form-control" type="text" v-model="groupSearch" :placeholder="_('Keyword search')" />
          </div>
          <div v-if="!filteredGroups.length" class="text-muted">{{ _('No user groups found.') }}</div>
          <div v-for="group in filteredGroups" :key="group.id" class="checkbox">
            <label>
              <input
                type="checkbox"
                :checked="isInGroup(group)"
                :disabled="!!parseInt(group.allusers) || saving"
                v-on:change="toggleGroupMembership(group, $event)"
              />
              <span>
                <strong>{{ group.name }}</strong>
                <span v-if="!!parseInt(group.allusers)" class="text-muted"> — {{ _('All users') }}</span>
                <span v-if="group.description" class="text-muted"> — {{ group.description }}</span>
              </span>
            </label>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h4>{{ _('User level') }}</h4>
          <hr />
          <div class="form-group">
            <select id="userLevel" v-model="editedUser.userlevel" class="form-control" :disabled="saving">
              <option value="1" v-if="editedUser.userlevel == 1">{{ _('Private sales channel user') }}</option>
              <option value="2">{{ _('POS user') }}</option>
              <option value="3">{{ _('Standard user') }}</option>
              <option value="7" v-if="userData && parseInt(userData.userlevel, 10) >= 7">{{ _('Administrator') }}</option>
              <option v-else disabled="disabled" value="7">{{ _('Administrator') }}</option>
              <option v-if="editedUser.userlevel == 10" value="10" disabled="disabled">{{ _('System Administrator') }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="row">
      <div class="col-md-12">
        <p class="text-muted">{{ _('Loading...') }}</p>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-if="editedUser && editedUser.id" v-on:click="remove" :disabled="saving"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'

export default {
  mixins: [components],
  data () {
    return {
      editedUser: null,
      allGroups: [],
      membershipIds: [],
      originalMembershipIds: [],
      originalUserlevel: null,
      groupSearch: '',
      saving: false
    }
  },
  computed: {
    filteredGroups () {
      const q = (this.groupSearch || '').trim().toLowerCase()
      if (!q) return this.allGroups
      return this.allGroups.filter(group => {
        const name = (group.name || '').toLowerCase()
        const description = (group.description || '').toLowerCase()
        return name.includes(q) || description.includes(q)
      })
    },
    isMembershipDirty () {
      if (!this.editedUser) return false
      const current = [...this.membershipIds].map(id => parseInt(id)).sort((a, b) => a - b)
      const original = [...this.originalMembershipIds].map(id => parseInt(id)).sort((a, b) => a - b)
      return current.join(',') !== original.join(',')
    },
    isUserlevelDirty () {
      if (!this.editedUser || this.originalUserlevel === null) return false
      return parseInt(this.editedUser.userlevel, 10) !== parseInt(this.originalUserlevel, 10)
    },
    hasChanges () {
      return this.isMembershipDirty || this.isUserlevelDirty
    },
    isSaveButtonEnabled () {
      return this.hasChanges && !this.saving && !this.saved && !this.loading
    }
  },
  methods: {
    isInGroup (group) {
      if (parseInt(group.allusers)) return true
      return this.membershipIds.some(id => parseInt(id) === parseInt(group.id))
    },
    storeOriginalMembership () {
      this.originalMembershipIds = [...this.membershipIds]
    },
    storeOriginalUserlevel () {
      if (!this.editedUser) return
      this.originalUserlevel = this.editedUser.userlevel
    },
    async loadUser (id) {
      if (!id) return
      this.loading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/users/' + id + '.json?apikey=' + this.apiKey)
        this.editedUser = response.data
        this.membershipIds = (this.editedUser.groupids || []).map(gid => parseInt(gid))
        this.storeOriginalUserlevel()
        await this.loadGroups()
        this.storeOriginalMembership()
      } catch (e) {
        this.showError(e)
      }
      this.loading = false
    },
    async loadGroups () {
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey)
        this.allGroups = response.data || []
      } catch (e) {
        this.allGroups = []
      }
    },
    toggleGroupMembership (group, event) {
      if (parseInt(group.allusers) || !this.editedUser) return
      const groupId = parseInt(group.id)
      const shouldBeInGroup = event.target.checked
      if (shouldBeInGroup) {
        if (!this.membershipIds.some(id => parseInt(id) === groupId)) {
          this.membershipIds = [...this.membershipIds, groupId]
        }
      } else {
        this.membershipIds = this.membershipIds.filter(id => parseInt(id) !== groupId)
      }
    },
    async saveMembershipChanges () {
      const current = this.membershipIds.map(id => parseInt(id))
      const original = this.originalMembershipIds.map(id => parseInt(id))
      const toAdd = current.filter(id => !original.includes(id))
      const toRemove = original.filter(id => !current.includes(id))
      for (const groupId of toAdd) {
        await axios.patch(
          this.baseDomain + this.shopId + '/usergroups/' + groupId + '.json?apikey=' + this.apiKey,
          { id: groupId, addMembers: [{ id: parseInt(this.editedUser.id) }] }
        )
      }
      for (const groupId of toRemove) {
        await axios.patch(
          this.baseDomain + this.shopId + '/usergroups/' + groupId + '.json?apikey=' + this.apiKey,
          { id: groupId, removeMembers: [parseInt(this.editedUser.id)] }
        )
      }
    },
    async saveUserlevel () {
      if (!this.isUserlevelDirty) return
      await axios.put(
        this.baseDomain + this.shopId + '/users/' + this.editedUser.id + '.json?apikey=' + this.apiKey,
        this.editedUser
      )
    },
    async save () {
      if (!this.editedUser || !this.hasChanges) return
      this.saving = true
      try {
        await this.saveMembershipChanges()
        await this.saveUserlevel()
        this.storeOriginalMembership()
        this.storeOriginalUserlevel()
        this.saved = true
        this.showNotification(this._('Changes saved successfully!'))
        this.EventBus.$emit('collectionUsersChanged')
        setTimeout(() => { this.saved = false }, 1000)
      } catch (e) {
        this.showError(e)
      }
      this.saving = false
    },
    async remove () {
      if (!this.editedUser || !this.editedUser.id) return
      if (!confirm(this._('Are you sure you want to delete the user? Deletion can\'t be undone.'))) return
      this.saving = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/users/' + this.editedUser.id + '.json?apikey=' + this.apiKey)
        this.EventBus.$emit('collectionUsersChanged')
        this.close()
      } catch (e) {
        this.showError(this._('Deleting the user failed!'))
      }
      this.saving = false
    }
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadUser(this.component.initData.userId)
    } else {
      this.loadGroups()
    }
  },
  async updated () {
    if (this.component && this.component.initData && !this.loading) {
      if (this.editedUser && this.editedUser.id && parseInt(this.editedUser.id, 10) !== parseInt(this.component.initData.userId, 10)) {
        try {
          await this.loadUser(this.component.initData.userId)
        } catch (e) {}
      }
    }
  }
}
</script>
